import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["disabled", "href", "icon", "rightIcon", "use", "loading", "_button", "_buttonOpened", "rel", "as", "focused"];

var _class, _class2, _temp;

import React from 'react';
import PropTypes from 'prop-types';
import { globalObject } from '@skbkontur/global-object';
import { keyListener } from "../../../lib/events/keyListener";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { isExternalLink } from "../../../lib/utils";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { rootNode } from "../../../lib/rootNode";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { ThemeFactory } from "../../../lib/theming/ThemeFactory";
import { isDarkTheme, isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { isIE11 } from "../../../lib/client";
import { ReactUIFeatureFlagsContext, getFullReactUIFlagsContext } from "../../../lib/featureFlagsContext";
import { getVisualStateDataAttributes } from "../../../internal/CommonWrapper/utils/getVisualStateDataAttributes";
import { globalClasses, styles } from "../Link.styles";
import { LinkIcon } from "../LinkIcon";
export var LinkDataTids = {
  root: 'Link__root'
};
export
/**
* Элемент ссылки из HTML.
*/
var Link = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Link, _React$Component);

  function Link() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Link.defaultProps);
    _this.state = {
      focusedByTab: false
    };

    _this.renderMain = function (props) {
      var disabled = props.disabled,
          href = props.href,
          icon = props.icon,
          rightIcon = props.rightIcon,
          use = props.use,
          loading = props.loading,
          _button = props._button,
          _buttonOpened = props._buttonOpened,
          relOrigin = props.rel,
          Component = props.as,
          _props$focused = props.focused,
          focused = _props$focused === void 0 ? false : _props$focused,
          rest = _objectWithoutPropertiesLoose(props, _excluded);

      var _isTheme2022 = isTheme2022(_this.theme);

      var arrow = null;

      if (_button) {
        arrow = /*#__PURE__*/React.createElement("span", {
          className: styles.arrow()
        });
      }

      var rel = relOrigin;

      if (typeof rel === 'undefined' && href) {
        rel = "noopener" + (isExternalLink(href) ? ' noreferrer' : '');
      }

      var isFocused = !disabled && (_this.state.focusedByTab || focused);
      var leftIconElement = icon && /*#__PURE__*/React.createElement(LinkIcon, {
        icon: icon,
        loading: loading,
        position: "left"
      });
      var rightIconElement = rightIcon && /*#__PURE__*/React.createElement(LinkIcon, {
        hasBothIcons: !!icon && !!rightIcon,
        icon: rightIcon,
        loading: loading,
        position: "right"
      });
      var linkProps = {
        className: cx(styles.useRoot(), use === 'default' && styles.useDefault(_this.theme), use === 'success' && styles.useSuccess(_this.theme), use === 'danger' && styles.useDanger(_this.theme), use === 'grayed' && styles.useGrayed(_this.theme), !!_button && styles.button(_this.theme), !!_buttonOpened && styles.buttonOpened(_this.theme), _this.getLinkClassName(isFocused, Boolean(disabled || loading), _isTheme2022)),
        href: href,
        rel: rel,
        onClick: _this.handleClick,
        onFocus: _this.handleFocus,
        onBlur: _this.handleBlur,
        tabIndex: disabled || loading ? -1 : _this.props.tabIndex
      };
      var child = _this.props.children;

      if (_isTheme2022) {
        var _cx, _cx2; // lineTextWrapper нужен для реализации transition у подчеркивания


        child = /*#__PURE__*/React.createElement("span", {
          className: cx(globalClasses.textWrapper, styles.lineTextWrapper(_this.theme), (_cx = {}, _cx[styles.lineTextWrapperFocused(_this.theme)] = isFocused, _cx))
        }, /*#__PURE__*/React.createElement("span", {
          className: cx(globalClasses.text, (_cx2 = {}, _cx2[styles.lineText(_this.theme)] = !isIE11, _cx2[styles.lineTextIE11(_this.theme)] = isIE11, _cx2))
        }, _this.props.children));
      }

      return /*#__PURE__*/React.createElement(Component, _extends({
        "data-tid": LinkDataTids.root
      }, rest, linkProps, getVisualStateDataAttributes({
        disabled: disabled
      })), leftIconElement, child, rightIconElement, arrow);
    };

    _this.handleFocus = function () {
      if (!_this.props.disabled) {
        // focus event fires before keyDown eventlistener
        // so we should check tabPressed in async way
        globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
          if (keyListener.isTabPressed) {
            _this.setState({
              focusedByTab: true
            });
          }
        });
      }
    };

    _this.handleBlur = function () {
      _this.setState({
        focusedByTab: false
      });
    };

    _this.handleClick = function (event) {
      var _this$props = _this.props,
          onClick = _this$props.onClick,
          disabled = _this$props.disabled,
          loading = _this$props.loading;

      var href = _this.getProps().href;

      if (!href) {
        event.preventDefault();
      }

      if (onClick && !disabled && !loading) {
        onClick(event);
      }
    };

    return _this;
  }

  var _proto = Link.prototype;

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ReactUIFeatureFlagsContext.Consumer, null, function (flags) {
      _this2.linkFocusOutline = getFullReactUIFlagsContext(flags).linkFocusOutline;
      return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
        _this2.theme = _this2.props.theme ? ThemeFactory.create(_this2.props.theme, theme) : theme;
        return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
          rootNodeRef: _this2.setRootNode
        }, _this2.getProps()), _this2.renderMain);
      });
    });
  };

  _proto.getLinkClassName = function getLinkClassName(focused, disabled, _isTheme2022) {
    var _this$getProps = this.getProps(),
        use = _this$getProps.use;

    var isBorderBottom = parseInt(this.theme.linkLineBorderBottomWidth) > 0;
    var isFocused = focused && !disabled;
    return !isBorderBottom ? cx(styles.root(this.theme), isFocused && styles.focus(this.theme), disabled && styles.disabled(this.theme), use === 'grayed' && focused && styles.useGrayedFocus(this.theme)) : cx(styles.lineRoot(), disabled && styles.disabled(this.theme), disabled && _isTheme2022 && isDarkTheme(this.theme) && styles.disabledDark22Theme(this.theme), isFocused && use === 'default' && styles.lineFocus(this.theme), isFocused && use === 'success' && styles.lineFocusSuccess(this.theme), isFocused && use === 'danger' && styles.lineFocusDanger(this.theme), isFocused && use === 'grayed' && styles.lineFocusGrayed(this.theme), isFocused && _isTheme2022 && this.linkFocusOutline && styles.focus2022(this.theme));
  };

  return Link;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'Link', _class2.displayName = 'Link', _class2.propTypes = {
  disabled: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
  use: PropTypes.oneOf(['default', 'success', 'danger', 'grayed'])
}, _class2.defaultProps = {
  href: '',
  use: 'default',
  as: 'a'
}, _temp)) || _class;